import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, X } from "lucide-react";

const SOURCEFORGE_URL = "https://sourceforge.net/software/product/TradeOff/";
const TRUSTRADIUS_URL = "https://www.trustradius.com/products/tradeoff";
const STORAGE_KEY = "bs_review_prompt";

// Subtle, once-only prompt shown to users who've hit a genuine success milestone
// (an Application for Payment approved or paid), inviting them to leave a public review.
const ReviewPrompt = ({ summary }) => {
  const [show, setShow] = useState(false);

  const happy = summary && ((summary.approved || 0) + (summary.paid || 0)) >= 1;

  useEffect(() => {
    if (!happy) return;
    if (localStorage.getItem(STORAGE_KEY)) return;
    const t = setTimeout(() => setShow(true), 2500);
    return () => clearTimeout(t);
  }, [happy]);

  const close = () => {
    try { localStorage.setItem(STORAGE_KEY, "done"); } catch (e) {}
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="fixed z-40 bottom-20 left-4 right-4 sm:right-auto lg:bottom-6 lg:left-[280px] sm:max-w-xs bg-white border border-slate-200 shadow-xl rounded-xl p-4"
          data-testid="review-prompt"
        >
          <button
            type="button"
            onClick={close}
            aria-label="Dismiss"
            data-testid="review-prompt-dismiss"
            className="absolute top-2.5 right-2.5 text-slate-400 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-1 text-amber-400 mb-1.5">
            {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-3.5 w-3.5 fill-current" />)}
          </div>
          <p className="font-display font-semibold text-slate-900 text-sm">Enjoying TradeOff?</p>
          <p className="text-xs text-slate-600 mt-1 leading-relaxed">
            You've had an application approved — nice one! A quick review helps other subcontractors find us. It takes a minute.
          </p>
          <div className="mt-3 flex flex-col gap-2">
            <a
              href={SOURCEFORGE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={close}
              data-testid="review-prompt-sourceforge"
              className="inline-flex items-center justify-center rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold h-9 transition-colors"
            >
              Review on SourceForge
            </a>
            <a
              href={TRUSTRADIUS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={close}
              data-testid="review-prompt-trustradius"
              className="inline-flex items-center justify-center rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold h-9 transition-colors"
            >
              Review on TrustRadius
            </a>
          </div>
          <button
            type="button"
            onClick={close}
            className="mt-2 w-full text-center text-[11px] text-slate-400 hover:text-slate-600"
          >
            Not now
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReviewPrompt;
